import React, { useState } from "react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { DefectFormFields } from "../defects/DefectFormFields";
import { AddListItemDialog } from "../defects/AddListItemDialog";
import { compressImage } from "./imageUtils";
import { handoverApi } from "./handoverApi";

const EMPTY = { area: "", element: "", issue: "", details: "", priority: "medium", assigned_to: "", due_date: null, images: [] };
const LABELS = { area: "Area", element: "Element", issue: "Issue" };

export const WalkthroughItemDialog = ({ open, onOpenChange, walkthroughId, siteName, dwellingNumber, options, onAdded }) => {
  const [item, setItem] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(null);
  const [newName, setNewName] = useState("");

  const onImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (item.images.length + files.length > 3) return toast.error("Maximum 3 photos per item", { position: "top-center" });
    const imgs = await Promise.all(files.map((f) => compressImage(f)));
    setItem((p) => ({ ...p, images: [...p.images, ...imgs] }));
  };

  const submitOption = async () => {
    const name = await options.addOption(adding, newName);
    if (name) { setItem((p) => ({ ...p, [adding]: name })); setAdding(null); setNewName(""); }
  };

  const save = async (keepOpen) => {
    if (!item.area || !item.element || !item.issue) return toast.error("Please choose Area, Element and Issue");
    setSaving(true);
    try {
      await handoverApi(`/walkthroughs/${walkthroughId}/items`, { method: "POST", body: {
        ...item, assigned_to: item.assigned_to || null,
        due_date: item.due_date ? format(item.due_date, "yyyy-MM-dd") : null } });
      toast.success("Item recorded", { position: "top-center" });
      setItem(EMPTY);
      onAdded();
      if (!keepOpen) onOpenChange(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="walkthrough-item-dialog">
          <DialogHeader>
            <DialogTitle>Record Item</DialogTitle>
            <DialogDescription>This item will be shared with the client.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3" data-testid="walkthrough-item-location">
            {[["Site", siteName, "site"], ["Dwelling", dwellingNumber, "dwelling"]].map(([label, val, id]) => (
              <div key={id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 min-w-0">
                <p className="text-xs font-medium text-slate-500">{label}</p>
                <p className="font-semibold text-slate-900 break-words" data-testid={`walkthrough-item-${id}`}>{val || "—"}</p>
              </div>
            ))}
          </div>
          <DefectFormFields value={item} setValue={setItem} areas={options.area} elements={options.element}
            issues={options.issue} contractors={options.contractors} showAddButtons unassignedValue=""
            onAddArea={() => setAdding("area")} onAddElement={() => setAdding("element")} onAddIssue={() => setAdding("issue")}
            imageInputId="walkthrough-item-images" onImageUpload={onImageUpload}
            onRemoveImage={(i) => setItem((p) => ({ ...p, images: p.images.filter((_, x) => x !== i) }))} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="walkthrough-item-cancel">Cancel</Button>
            <Button variant="outline" disabled={saving} onClick={() => save(true)} className="border-amber-300" data-testid="walkthrough-item-save-another">
              Save & Add Another
            </Button>
            <Button disabled={saving} onClick={() => save(false)} className="btn-construction" data-testid="walkthrough-item-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AddListItemDialog open={!!adding} onOpenChange={(o) => !o && setAdding(null)}
        title={`Add New ${LABELS[adding] || ""}`} description="Adds it to your list for future use."
        label={`${LABELS[adding] || ""} Name *`} placeholder="" value={newName} onChange={setNewName}
        onSubmit={submitOption} onCancel={() => { setAdding(null); setNewName(""); }} submitLabel="Add" />
    </>
  );
};
